import { toast, ToastOptions } from 'react-toastify';

export interface ToastConfig extends ToastOptions {
  duration?: number;
}

const defaultConfig: ToastOptions = {
  position: 'top-right',
  autoClose: 4000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  theme: 'light',
};

export const useToast = () => {
  const showSuccess = (message: string, config?: ToastConfig) => {
    const finalConfig = { 
      ...defaultConfig, 
      ...config,
      autoClose: config?.duration || config?.autoClose || defaultConfig.autoClose 
    };
    return toast.success(message, finalConfig);
  };

  const showError = (message: string, config?: ToastConfig) => {
    const finalConfig = { 
      ...defaultConfig, 
      ...config,
      autoClose: config?.duration || config?.autoClose || 5000 
    };
    return toast.error(message, finalConfig);
  };

  const showWarning = (message: string, config?: ToastConfig) => {
    const finalConfig = { 
      ...defaultConfig, 
      ...config,
      autoClose: config?.duration || config?.autoClose || defaultConfig.autoClose 
    };
    return toast.warning(message, finalConfig);
  };

  const showInfo = (message: string, config?: ToastConfig) => {
    const finalConfig = { 
      ...defaultConfig, 
      ...config,
      autoClose: config?.duration || config?.autoClose || defaultConfig.autoClose 
    };
    return toast.info(message, finalConfig);
  };

  const showLoading = (message: string) => {
    return toast.loading(message);
  };

  const dismissToast = (toastId?: string | number) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  };

  const updateToast = (toastId: string | number, options: ToastOptions) => {
    toast.update(toastId, options);
  };

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    dismissToast,
    updateToast,
  };
};

export const toastUtils = {
  success: (message: string, config?: ToastConfig) => {
    const finalConfig = { 
      ...defaultConfig, 
      ...config,
      autoClose: config?.duration || config?.autoClose || defaultConfig.autoClose 
    };
    return toast.success(message, finalConfig);
  },
  error: (message: string, config?: ToastConfig) => {
    const finalConfig = { 
      ...defaultConfig, 
      ...config,
      autoClose: config?.duration || config?.autoClose || 5000 
    };
    return toast.error(message, finalConfig);
  },
  warning: (message: string, config?: ToastConfig) => {
    const finalConfig = { 
      ...defaultConfig, 
      ...config,
      autoClose: config?.duration || config?.autoClose || defaultConfig.autoClose 
    };
    return toast.warning(message, finalConfig);
  },
  info: (message: string, config?: ToastConfig) => {
    const finalConfig = { 
      ...defaultConfig, 
      ...config,
      autoClose: config?.duration || config?.autoClose || defaultConfig.autoClose 
    };
    return toast.info(message, finalConfig);
  },
  loading: (message: string) => {
    return toast.loading(message);
  },
  dismiss: (toastId?: string | number) => {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  },
  promise: <T,>(
    promise: Promise<T>,
    messages: {
      pending: string;
      success: string;
      error: string;
    },
    config?: ToastConfig
  ) => {
    return toast.promise(promise, messages, {
      ...defaultConfig,
      ...config,
    });
  },
};

export default useToast;
